import React, { useEffect, useRef, useMemo, useState } from "react";
import { createChart, CrosshairMode, LineSeries, IChartApi, ISeriesApi, ColorType } from "lightweight-charts";
import { ChartOptions, DeepPartial } from "lightweight-charts";
import styles from './2DChart.module.css';
import logger from '../../../logging/logger';

/**
 * Data format expected:
 * {
 *   Dates: string[],           // x-axis values in 'yyyy-mm-dd' format
 *   Prices: number[],          // y-axis values (or any other key name like Values, Returns, etc.)
 *   Description?: string,      // Chart title (optional)
 *   // Additional keys can be included for extra data series (multi-trace support)
 * }
 *
 * Multi-trace support:
 * - Each non-reserved key with array values becomes a separate trace
 * - Example: { Dates: [...], SPY: [...], NVDA: [...] } creates 2 traces
 */
interface ChartData {
  Dates?: string[];
  Description?: string;
  [key: string]: string[] | number[] | string | undefined;
}

interface EodChartProps {
  data?: ChartData;
  height?: number | string;
  valueLabel?: string;  // Optional label for y-axis (overrides auto-detection)
  referenceLines?: Array<{ value: number; label?: string; color?: string }>;
}

// Theme colors with multi-trace palette
const THEME = {
  chartBackground: "#0a0b0b",
  chartText: "#e2e8f0",
  chartGrid: "#1f2937",
  chartScaleBorder: "#334155",
  lineColors: [
    "#2962FF",
    "#FF6D00",
    "#00C853",
    "#9ca3af",
    "#FF1744",
    "#00BCD4",
    "#FFD600",
    "#E91E63",
  ],
};

function getChartOptions(): DeepPartial<ChartOptions> {
  return {
    layout: {
      background: { type: ColorType.Solid, color: THEME.chartBackground },
      textColor: THEME.chartText,
    },
    grid: {
      vertLines: { color: THEME.chartGrid },
      horzLines: { color: THEME.chartGrid },
    },
    rightPriceScale: { borderColor: THEME.chartScaleBorder },
    timeScale: {
      borderColor: THEME.chartScaleBorder,
      allowBoldLabels: true,
    },
    handleScroll: {
      mouseWheel: true,
      pressedMouseMove: true,
      horzTouchDrag: true,
      vertTouchDrag: false,
    },
    handleScale: {
      axisPressedMouseMove: true,
      mouseWheel: true,
      pinch: true,
    },
    crosshair: { mode: CrosshairMode.Normal },
    localization: { locale: "en-US" },
  };
}

/**
 * Reserved keys that represent date/time axis or metadata (not data series).
 * Includes 'date' (singular) for LLM resilience - some models may output singular form.
 */
const DATE_RESERVED_KEYS = ['Dates', 'dates', 'date', 'Date', 'Description', 'description'];

/**
 * Gets all data keys (non-reserved keys with array values)
 * For multi-trace charts, each key becomes a separate trace
 */
function getDataKeys(data: ChartData): string[] {
  return Object.keys(data).filter(
    key => !DATE_RESERVED_KEYS.includes(key) && Array.isArray(data[key])
  );
}

/**
 * Checks if a value is a valid number (not null, undefined, NaN, or Infinity)
 */
function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' &&
         !Number.isNaN(value) &&
         Number.isFinite(value);
}

/**
 * Transforms data for a specific series key into lightweight-charts format.
 * Handles 'date' (singular) as fallback for LLM resilience.
 */
function transformSeriesData(
  data: ChartData,
  seriesKey: string
): Array<{ time: string; value: number }> {
  // Check for dates in order of preference: Dates, dates, Date, date
  const dates = data.Dates || data.dates || (data as Record<string, unknown>).Date || (data as Record<string, unknown>).date;
  if (!dates || !Array.isArray(dates)) {
    logger.warn('2DChart: No dates array found in data', { keys: Object.keys(data) });
    return [];
  }

  const values = data[seriesKey] as number[];
  if (!values || !Array.isArray(values)) return [];

  return (dates as string[])
    .map((time, index) => ({
      time: time as string,
      value: values[index],
    }))
    .filter(point => point.time && isValidNumber(point.value));
}

export const EodChart: React.FC<EodChartProps> = ({ data = {}, height, valueLabel, referenceLines }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesMapRef = useRef<Map<string, ISeriesApi<"Line">>>(new Map());

  // Get all data series keys
  const dataKeys = useMemo(() => getDataKeys(data), [data]);
  const isMultiTrace = dataKeys.length > 1;

  // Track which legend item is hovered (null = none, show all normally)
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  // Track crosshair values for each series (updated on hover/click)
  const [crosshairValues, setCrosshairValues] = useState<Record<string, number | null>>({});

  // Debug: log data received for chart
  logger.info('EodChart: Received data', {
    data,
    dataKeys,
    isMultiTrace,
    allKeys: Object.keys(data),
  });

  // Derive chart title and y-axis label from data
  const chartTitle = data.Description || '';
  const yAxisLabel = valueLabel || (dataKeys.length === 1 ? dataKeys[0] : null);

  // Mount: create chart (series are created dynamically based on data)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const chart = createChart(el, getChartOptions());
    chartRef.current = chart;

    // Handle resize
    const resize = () => {
      const r = el.getBoundingClientRect();
      chart.applyOptions({
        width: Math.max(0, Math.floor(r.width || 600)),
        height: Math.max(0, Math.floor(r.height || 400)),
      });
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(el);

    // Subscribe to crosshair move to update legend values
    // Only update on actual mouse movement, not scroll/zoom
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || param.point.x < 0 || param.point.y < 0) {
        // Mouse left the chart or scroll/zoom event — clear values
        setCrosshairValues({});
        return;
      }
      if (!param.time) {
        setCrosshairValues({});
        return;
      }
      const values: Record<string, number | null> = {};
      seriesMapRef.current.forEach((series, key) => {
        const data = param.seriesData.get(series);
        if (data && 'value' in data) {
          values[key] = (data as { value: number }).value;
        } else {
          values[key] = null;
        }
      });
      setCrosshairValues(values);
    });

    // Capture ref values for cleanup to avoid stale closure
    const currentSeriesMapRef = seriesMapRef.current;
    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      currentSeriesMapRef.clear();
    };
  }, []);

  // Update series when data changes (supports multi-trace)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const currentKeys = new Set(dataKeys);
    const existingKeys = Array.from(seriesMapRef.current.keys());

    // Remove series that no longer exist in data
    existingKeys.forEach(key => {
      if (!currentKeys.has(key)) {
        const series = seriesMapRef.current.get(key);
        if (series) {
          chart.removeSeries(series);
          seriesMapRef.current.delete(key);
        }
      }
    });

    // Add/update series for each data key
    dataKeys.forEach((key, index) => {
      const color = THEME.lineColors[index % THEME.lineColors.length];
      let series = seriesMapRef.current.get(key);

      if (!series) {
        // Create new series
        series = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: true,
          title: isMultiTrace ? key : undefined,  // Show legend label for multi-trace
        });
        seriesMapRef.current.set(key, series);
      }

      // Update series data
      const seriesData = transformSeriesData(data, key);
      series.setData(seriesData);

      // Apply price format
      const isPercent = key.toLowerCase().includes('return') ||
                        key.toLowerCase().includes('percent') ||
                        key.toLowerCase().includes('%') ||
                        valueLabel?.toLowerCase().includes('return') ||
                        valueLabel?.toLowerCase().includes('%');

      series.applyOptions({
        priceFormat: {
          type: "custom" as const,
          formatter: (price: number) => {
            if (isPercent) {
              const percentValue = price * 100;
              return `${percentValue.toFixed(2)}%`;
            }
            return price.toFixed(2);
          },
          minMove: 0.0001,
        },
      });
    });

    chart.timeScale().fitContent();
  }, [data, dataKeys, isMultiTrace, valueLabel]);

  // Add horizontal reference lines (e.g., RSI 70/30 levels)
  useEffect(() => {
    if (!referenceLines || referenceLines.length === 0) return;
    // Add price lines to the first series
    const firstSeries = seriesMapRef.current.values().next().value;
    if (!firstSeries) return;

    // Remove existing price lines by recreating them
    // lightweight-charts doesn't have removePriceLine by reference easily,
    // so we track and recreate on change
    const lines = referenceLines.map(line => {
      return firstSeries.createPriceLine({
        price: line.value,
        color: line.color || '#787B86',
        lineWidth: 1,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: line.label || '',
      });
    });

    return () => {
      lines.forEach(line => {
        try { firstSeries.removePriceLine(line); } catch (_) {}
      });
    };
  }, [referenceLines, dataKeys]);

  // Highlight hovered series, dim others
  useEffect(() => {
    seriesMapRef.current.forEach((series, key) => {
      const index = dataKeys.indexOf(key);
      const baseColor = THEME.lineColors[index % THEME.lineColors.length];
      if (hoveredSeries === null) {
        // No hover — all series normal
        series.applyOptions({ color: baseColor, lineWidth: 2 });
      } else if (key === hoveredSeries) {
        // Hovered series — bold
        series.applyOptions({ color: baseColor, lineWidth: 3 });
      } else {
        // Other series — dim
        series.applyOptions({ color: baseColor + '40', lineWidth: 1 });
      }
    });
  }, [hoveredSeries, dataKeys]);

  return (
    <div className={styles.chartWrap} style={{ height: height || "400px" }}>
      {chartTitle && (
        <div className={styles.chartTitle}>{chartTitle}</div>
      )}
      <div className={styles.chartContainer} ref={wrapRef} />
      {/* Legend for multi-trace charts */}
      {isMultiTrace && (
        <div className={styles.legend} onMouseLeave={() => setHoveredSeries(null)}>
          {dataKeys.map((key, index) => {
            const crosshairVal = crosshairValues[key];
            const isPercent = key.toLowerCase().includes('return') ||
                              key.toLowerCase().includes('percent') ||
                              key.toLowerCase().includes('%') ||
                              valueLabel?.toLowerCase().includes('return') ||
                              valueLabel?.toLowerCase().includes('%');
            const formattedValue = crosshairVal != null
              ? isPercent ? `${(crosshairVal * 100).toFixed(2)}%` : crosshairVal.toFixed(2)
              : null;
            return (
              <div
                key={key}
                className={`${styles.legendItem} ${hoveredSeries !== null && hoveredSeries !== key ? styles.legendItemDimmed : ''}`}
                onMouseEnter={() => setHoveredSeries(key)}
              >
                <span
                  className={styles.legendColor}
                  style={{ backgroundColor: THEME.lineColors[index % THEME.lineColors.length] }}
                />
                <span className={styles.legendLabel}>{key}</span>
                {formattedValue != null && (
                  <span className={styles.legendValue}>{formattedValue}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {yAxisLabel && !isMultiTrace && (
        <div className={styles.yAxisLabel}>{yAxisLabel}</div>
      )}
    </div>
  );
};